import type { ReactElement } from "react";
import { Toast } from "@primitives/index";
import { AskQuestionDialog, CreateWorkflowDialog } from "@compositions/index";
import type { AskQuestionDialogProps } from "@compositions/index";
import type { NextDispenser } from "./useNextDispenser";
import type { WorkflowDialogs } from "./useWorkflowDialogs";

export interface WorkflowOverlaysProps {
  readonly next: NextDispenser;
  readonly dialogs: WorkflowDialogs;
  readonly queueTree: AskQuestionDialogProps["queueTree"];
  readonly sendAsQueues: AskQuestionDialogProps["sendAsQueues"];
  readonly excludeQueueIdsForAsk: readonly string[];
  readonly askOriginatingQueueId: string | undefined;
}

/**
 * The viewport-fixed layers of the workflow layout: the "Next" notice and
 * the two spawn dialogs. They are siblings of the scene stack, never
 * children of it, so a scene transition cannot move them and a scene
 * switch cannot remount them.
 */
export function WorkflowOverlays({
  next,
  dialogs,
  queueTree,
  sendAsQueues,
  excludeQueueIdsForAsk,
  askOriginatingQueueId,
}: WorkflowOverlaysProps): ReactElement {
  return (
    <>
      <Toast
        open={next.notice !== null}
        message={next.notice?.text ?? ""}
        tone={next.notice?.tone ?? "info"}
        onDismiss={next.dismissNotice}
        autoHideMs={4500}
        testId="next-notice"
      />

      <CreateWorkflowDialog
        isOpen={dialogs.create.isOpen}
        isSubmitting={dialogs.create.isSubmitting}
        errorMessage={dialogs.create.error}
        availableKinds={dialogs.create.availableKinds}
        entityCandidates={dialogs.create.entityCandidates}
        entityCandidatesLoading={dialogs.create.entityCandidatesLoading}
        onEntitySearch={dialogs.create.onEntitySearch}
        entityResolveStatus={dialogs.create.entityResolveStatus}
        entityResolveLoading={dialogs.create.entityResolveLoading}
        onEntityResolve={dialogs.create.onEntityResolve}
        existingWorkflows={dialogs.create.existingWorkflows}
        existingWorkflowsLoading={dialogs.create.existingWorkflowsLoading}
        onOpenExisting={dialogs.create.onOpenExisting}
        onClose={dialogs.create.close}
        onSubmit={dialogs.create.submit}
      />

      <AskQuestionDialog
        isOpen={dialogs.ask.isOpen}
        isSubmitting={dialogs.ask.isSubmitting}
        errorMessage={dialogs.ask.error}
        queueTree={queueTree}
        excludeQueueIds={excludeQueueIdsForAsk}
        {...(sendAsQueues !== undefined ? { sendAsQueues } : {})}
        {...(askOriginatingQueueId !== undefined
          ? { originatingQueueId: askOriginatingQueueId }
          : {})}
        onClose={dialogs.ask.close}
        onSubmit={dialogs.ask.submit}
      />
    </>
  );
}
